import { useCallback } from 'react';
import { common_v1_ObjectMeta } from '@antimatterhq/asl-api-ts-v1';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const exhaustiveMatchingGuard = (_: never): never => {
  throw new Error('Should not be reached');
};

export const bumpMetadataVersion = (draft: {
  metadata?: common_v1_ObjectMeta | null;
}) => {
  if (!draft.metadata) {
    draft.metadata = { version: 0, name: '' };
  }
  draft.metadata.version = (draft.metadata?.version || 0) + 1;
};

export const getFormikNames = <T extends string>(
  shape: object,
  prefix: string,
) => {
  const names = Object.keys(shape).reduce(
    (acc, key) => ({
      ...acc,
      [key]: `${prefix}${key}`,
    }),
    {} as { [K in T]: `${typeof prefix}${K}` },
  );

  return names;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const removeObjectNulls = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj
      .map((item) => removeObjectNulls(item))
      .filter((item) => item !== null && item !== '');
  } else if (obj !== null && typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cleanedObj: { [key: string]: any } = {};

    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = removeObjectNulls(value);

      // Only add the key if the cleaned value is not null or an empty string
      if (cleanedValue !== null && cleanedValue !== '') {
        cleanedObj[key] = cleanedValue;
      }
    }

    return cleanedObj;
  }

  // Return the value if it's neither null nor an empty string
  return obj;
};

export const cleanObject = <T>(object: T): T => {
  return removeObjectNulls(object);
};

interface UseOnKeyDownProps {
  enabled?: boolean;
  onEnter?: () => void;
  onEscape?: () => void;
  // Add more key handlers as needed
}

export const useOnKeyDown = ({
  enabled = true,
  onEnter,
  onEscape,
  // ... other handlers
}: UseOnKeyDownProps) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!enabled) return;

      switch (e.key) {
        case 'Enter':
          onEnter?.();
          break;
        case 'Escape':
          onEscape?.();
          break;
        // Add more cases as needed
      }
    },
    [enabled, onEnter, onEscape],
  );

  return { handleKeyDown };
};

/**
 * Converts an array of key-value pairs to an object. Typically from our MultiValues component.
 * @param keyValueArray - The array of key-value pairs.
 * @returns The object with the key-value pairs.
 */
export const keyValueArrayToObject = (
  keyValueArray: { key?: string | undefined; value?: string | undefined }[],
) => {
  const filtered = keyValueArray.filter(({ key, value }) => key && value);
  if (!filtered.length) {
    return undefined;
  }
  return filtered.reduce(
    (acc, { key, value }) => ({
      ...acc,
      [key || '']: value,
    }),
    {},
  );
};
